import * as StateLib from "./StateLib";
import { GetUniqueLocalValuesForField } from "./DataLib";
import { isNullish } from "@theorgchartdev/storybook";
import { Globals } from "@coreapp/CoreApp/Globals";
import { enumBoxStyleSet, enumUpdatePlanModes } from "@coreapp/constants";
import { QAlert } from "@coreapp/CoreApp/QAlert";
import { QGetUserListRFC } from "@coreapp/CoreApp/QGetUserListRFC";
import { QQueryUniqueValues } from "@coreapp/CoreApp/QQueryUniqueValues";
import { QSvrPersonQuery } from "@coreapp/CoreApp/QSvrPersonQuery";
import { QQueryChartMetadata } from "@coreapp/CoreApp/QQueryChartMetadata";
import { QDataRefreshRFC } from "@coreapp/CoreApp/QDataRefreshRFC";
import { QGetAccountInfoRFC } from "@coreapp/CoreApp/QGetAccountInfoRFC";
import { QGetBoxStylesRFC } from "@coreapp/CoreApp/QGetBoxStylesRFC";
import { QUploadBoxStylesRFC } from "@coreapp/CoreApp/QUploadBoxStylesRFC";
import { QDirRFCJSON } from "@coreapp/CoreApp/QDirRFCJSON";
import { QUpdatePassRFC } from "@coreapp/CoreApp/QUpdatePassRFC";
import { QValidateCredsRFC } from "@coreapp/CoreApp/QValidateCredsRFC";
import { QTestSharepointConnectionRFC } from "@coreapp/CoreApp/QTestSharepointConnectionRFC";
import { QLoadHeadersRFC } from "@coreapp/CoreApp/QLoadHeadersRFC";
import { QSSOConfigRFC } from "@coreapp/CoreApp/QSSOConfigRFC";
import { QScimTokenRFC } from "@coreapp/CoreApp/QScimTokenRFC";
import { QBuildTableRFC } from "@coreapp/CoreApp/QBuildTableRFC";
import { QRecordLookupRFC } from "@coreapp/CoreApp/QRecordLookupRFC";
import { QBuildPlanRFC } from "@coreapp/CoreApp/QBuildPlanRFC";
import { QUpdatePlansRFC } from "@coreapp/CoreApp/QUpdatePlansRFC";
import { QDeleteUsersRFC } from "@coreapp/CoreApp/QDeleteUsersRFC";
import { QAuditReportRFC } from "@coreapp/CoreApp/QAuditReportRFC";
import { QAuditSnapshotRFC } from "@coreapp/CoreApp/QAuditSnapshotRFC";
import { QAuditMetaRFC } from "@coreapp/CoreApp/QAuditMetaRFC";
import { QReportsRFC } from "@coreapp/CoreApp/QReportsRFC";
import { QFileRFC } from "@coreapp/CoreApp/QFileRFC";
import { checkMaster } from "@coreapp/utils/checkMaster";
import { checkSSOConfig } from "@coreapp/utils/checkSSOConfig";
import { checkCheckPoints } from "@coreapp/utils/checkCheckPoints";
import { appstr } from "@coreapp/utils/appstr";
import { ProcessFolderList } from "@coreapp/utils/ProcessFolderList";
import { checkPlans } from "@coreapp/utils/checkPlans";
import { GetDoc, GetWSMgr } from "./StateLib";
import { QDownloadAppendTableRFC } from "@coreapp/CoreApp/QDownloadAppendTableRFC";
import { QGetPlanSettings } from "@coreapp/CoreApp/QGetPlanSettings";
import { QUpdatePlanSettings } from "@coreapp/CoreApp/QUpdatePlanSettings";

// USER RFCs self=true returns single user information
export function GetUserList(self) {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      reject();
      return;
    }
    const getUserDataRFC = new QGetUserListRFC();
    const callbackObject = {
      GetUserListResult: (success, userList, groupInfo, mfaInfo) => {
        if (success) {
          resolve({ userList, groupInfo, mfaInfo });
        } else {
          reject();
        }
      }
    };
    getUserDataRFC.InitGetUserListRFC(callbackObject);
    getUserDataRFC.SetParams(wsMgr.GetAccountName(), wsMgr.GetUserName(), self);
    Globals._rfcMgr.QueueRFC(getUserDataRFC);
  });
}

// CHART DATA RFCs
// TODO use this in FilterAccordionGroup
export function GetValuesForField(theField, getFromChartID = null, isArchive = false) {
  return new Promise((resolve, reject) => {
    const doc = StateLib.GetDoc();
    const chartID = getFromChartID ?? doc?.GetChartID();
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr || !chartID) {
      reject();
      return;
    }

    const frontEndDrivenFields = ["ReportsToName", "SubChart Level"];
    if (frontEndDrivenFields.includes(theField)) {
      const values = GetUniqueLocalValuesForField(theField);
      resolve({ results: values });
      return;
    }

    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    const queryUniques = new QQueryUniqueValues();
    const callbackObject = {
      DoTransactionFailed: () => {
        resolve({ results: [], success: false });
      },
      QueryUniqueValuesResponse: (success, fieldName, index, results) => {
        resolve({ success, fieldName, index, results });
      }
    };
    queryUniques.InitQueryUniques(callbackObject);
    queryUniques.fIncludeRawVals = true;
    queryUniques.SetParams(accountName, user, theField, chartID, undefined, undefined, isArchive);

    Globals._rfcMgr.QueueRFC(queryUniques);
  });
}

// SEARCH RECORDS RFC;
// query can be string or object of field name : field value string key/values
// query as obj for advanced search and query using string for simple search
// project fields can be list of field names where resultant records will return containing those fields
// project fields defaults to name, title, personid, supervisorid on backend
/**
 *
 * @param {string | object} queryDictOrString
 * @param {*} param1
 * @returns {Promise<any>}
 */
export function GetSearchResults(
  queryDictorString,
  {
    chartID,
    filters,
    projectFields,
    mode,
    reportId,
    planID = null,
    outsidePlan,
    includeAux,
    includeDeletes,
    isArchive,
    handlePositions = false,
    getHoldersWithNoPosition = false,
    sort,
    cursor,
    offset,
    pageSize,
    columnFilter,
    filterState,
    positionFilters,
    employeeFilters,
    holderProject,
    findPageByEntity
  } = {}
) {
  return new Promise((resolve, reject) => {
    const doc = StateLib.GetDoc();
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      reject();
      return;
    }

    let options = {};
    const queryChartID = chartID ?? doc?.GetChartID();
    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    if (projectFields) options["project"] = projectFields;
    if (holderProject) options["holderProject"] = holderProject;
    if (filters) options["filters"] = filters;
    if (mode) options["mode"] = mode;
    if (reportId) options["reportId"] = reportId;
    if (!isNullish(outsidePlan)) options["outsidePlan"] = outsidePlan;
    if (!isNullish(includeAux)) options["includeAux"] = includeAux;
    if (!isNullish(includeDeletes)) options["includeDeletes"] = includeDeletes;
    if (isArchive) options["isArchive"] = isArchive;
    if (sort) options["sort"] = sort;
    if (cursor) options["cursor"] = cursor;
    if (pageSize > 0) options["pageSize"] = pageSize;
    if (offset >= 0) options["offset"] = offset;
    if (!isNullish(columnFilter)) options["columnFilter"] = columnFilter;
    if (!isNullish(filterState)) options["filterState"] = filterState;
    if (!isNullish(positionFilters)) options["positionFilters"] = positionFilters;
    if (!isNullish(employeeFilters)) options["employeeFilters"] = employeeFilters;
    if (!isNullish(findPageByEntity)) options["findPageByEntity"] = findPageByEntity;

    const svrPersonQuery = new QSvrPersonQuery();
    const callbackObject = {
      SvrQueryResult: (success, result) => {
        if (success) {
          resolve({ result });
        } else {
          reject();
        }
      }
    };
    svrPersonQuery.InitServerPersonQuery(callbackObject);
    svrPersonQuery.AddParameter("options", JSON.stringify(options));
    svrPersonQuery.SetParams(
      accountName,
      user,
      queryDictorString,
      planID,
      queryChartID,
      handlePositions,
      getHoldersWithNoPosition
    );
    Globals._rfcMgr.QueueRFC(svrPersonQuery);
  });
}

/**
 * @param {string | null} chartID
 * @returns {Promise<{ result: any }>}
 */
export function GetPlanSettings(chartID) {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    const doc = StateLib.GetDoc();
    if (!wsMgr || !doc) {
      reject();
      return;
    }

    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    const chartId = !chartID && doc.GetChartID() ? doc.GetChartID() : chartID;

    const query = new QGetPlanSettings();
    const callbackObject = {
      GetPlanSettingsResult: (success, result) => {
        if (success) {
          resolve({ result });
        } else {
          reject();
        }
      }
    };

    query.InitGetPlanSettings(callbackObject);
    query.SetParams(accountName, user, chartId);
    Globals._rfcMgr.QueueRFC(query);
  });
}

/**
 * @param {string | null} chartID
 * @param {string} payload
 * @returns {Promise<{ success: boolean, result: any }>}
 */
export function UpdatePlanSettings(chartID, payload) {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    const doc = StateLib.GetDoc();
    if (!wsMgr || !doc) {
      reject();
      return;
    }

    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    const chartId = !chartID && doc.GetChartID() ? doc.GetChartID() : chartID;

    const query = new QUpdatePlanSettings();
    const callbackObject = {
      HandleResponse: (success, result) => {
        if (success) {
          resolve({ success, result });
        } else {
          reject();
        }
      }
    };

    query.InitUpdatePlanSettings(callbackObject);
    query.SetParams(accountName, user, chartId, payload);
    Globals._rfcMgr.QueueRFC(query);
  });
}

// chart settings rfc
/**
 * @param {string | null} chartID
 * @param {boolean} includeDataSourceConfig - admin Chart Settings only; returns
 *   unstripped data-source connection config (credentials). Defaults to false so
 *   every other caller receives the stripped config.
 * @returns {Promise<{ result: any }>}
 */
export function GetChartMetadata(chartID = null, includeDataSourceConfig = false) {
  return new Promise((resolve, reject) => {
    const doc = StateLib.GetDoc();
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      reject();
      return;
    }

    let jsonData = {};
    let settings = {};

    settings["type"] = "serverchart";
    if (includeDataSourceConfig) settings["includeDataSourceConfig"] = true;
    jsonData["settings"] = settings;

    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    if (chartID == null && doc && doc.GetChartID()) chartID = doc.GetChartID();

    const chartMetadataQuery = new QQueryChartMetadata();
    const callbackObject = {
      ChartMetadataResult: (success, result, response) => {
        if (success) {
          resolve({ result });
        } else if ("code" in response && response.code === "0011") {
          // If the account has no configured charts, do not throw error
          resolve({ result: null });
        } else {
          reject();
        }
      }
    };

    chartMetadataQuery.InitQueryChartData(callbackObject);
    chartMetadataQuery.SetParams(accountName, user, jsonData, chartID);
    Globals._rfcMgr.QueueRFC(chartMetadataQuery);
  });
}

/**
 *
 * @param {string | null} chartID
 * @param {*} theData
 * @param {boolean} doPostSaveRefresh
 * @returns {Promise<{ success: boolean, result: any }>}
 */
export function UpdateChartMetadata(chartID = null, theData, doPostSaveRefresh = false) {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr || !chartID) {
      reject();
      return;
    }

    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    const syncRFC = new QDataRefreshRFC();
    syncRFC.fReactCallback = true;
    const callbackObject = {
      HandleResponse: (success, result) => {
        if (success) {
          resolve({ success, result });
        } else {
          reject();
        }
      }
    };

    syncRFC.InitQDataRefreshQuery(callbackObject);
    syncRFC.fPostRefresh = doPostSaveRefresh;
    syncRFC.SetParamsExplicit(accountName, user, "chartMetaUpdate", theData, chartID);
    Globals._rfcMgr.QueueRFC(syncRFC);
  });
}

// account settings rfc
export function GetAccountMetadata(chartID = null) {
  return new Promise((resolve, reject) => {
    const doc = GetDoc();
    const wsMgr = GetWSMgr();
    if (!wsMgr) {
      reject();
      return;
    }

    let jsonData = {};
    let settings = {};

    settings["type"] = "serverchart";
    jsonData["settings"] = settings;

    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    if (chartID == null && doc && doc.GetChartID()) chartID = doc.GetChartID();

    const accountMetadataQuery = new QGetAccountInfoRFC();
    const callbackObject = {
      GetAccountInfoResult: result => {
        if (result) {
          resolve({ result });
        } else {
          reject();
        }
      }
    };

    accountMetadataQuery.InitGetAccountInfoRFC(callbackObject);
    accountMetadataQuery.SetParams(accountName, user);
    Globals._rfcMgr.QueueRFC(accountMetadataQuery);
  });
}

export function UpdateAccountMetadata(theData) {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      reject();
      return;
    }

    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    const syncRFC = new QDataRefreshRFC();
    syncRFC.fReactCallback = true;
    const callbackObject = {
      HandleResponse: (success, result) => {
        if (success) {
          resolve({ success, result });
        } else {
          reject();
        }
      }
    };

    syncRFC.InitQDataRefreshQuery(callbackObject);
    syncRFC.SetParamsNoChart(accountName, user, "metaUpdate", theData);
    Globals._rfcMgr.QueueRFC(syncRFC);
  });
}

export function LoadBoxStyles() {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      reject();
      return;
    }

    /*
    if (wsMgr.BoxStylesDirty()) {
      const alertPanel = new QAlert();
      alertPanel.InitConfirmAlert(
        appstr("Alert"),
        appstr("bsreload1") + "\n" + appstr("bsreload2"),
        null,
        wsMgr,
        "OKReloadBoxStyles",
        "CnclReloadBoxStyles"
      );
      return;
    }
     */

    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    var mode =
      wsMgr.GetBoxStylesMgr().CurrentBoxStyleSet() === enumBoxStyleSet.sharedboxstyles
        ? "getsharedstyles"
        : "getuserstyles";

    const getBoxSRFC = new QGetBoxStylesRFC();
    const callbackObject = {
      DownloadBoxStylesResult: (success, result) => {
        if (success) {
          resolve({ success, result });
        } else {
          reject();
        }
      }
    };
    getBoxSRFC.InitRFC(callbackObject);
    getBoxSRFC.SetParams(accountName, user, mode);
    Globals._rfcMgr.QueueRFC(getBoxSRFC);
  });
}

export function SaveBoxStyles() {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      reject();
      return;
    }

    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();
    const bsMgr = wsMgr.GetBoxStylesMgr();
    let mode = "";
    let data = "";

    if (bsMgr.CurrentBoxStyleSet() === enumBoxStyleSet.sharedboxstyles) {
      const styleList = bsMgr.SaveToJSON(bsMgr.fSharedBoxStyles);
      const jsonBuffer = { UID: bsMgr.fUID, styles: styleList };
      data = JSON.stringify(jsonBuffer);
      mode = "savesharedstyles";
    } else {
      const styleList = bsMgr.SaveToJSON(bsMgr.fUserBoxStyles);
      const jsonBuffer = { UID: bsMgr.fPUID, styles: styleList };
      data = JSON.stringify(jsonBuffer);
      mode = "saveuserstyles";
    }

    const uploadRFC = new QUploadBoxStylesRFC(success => {
      if (success) {
        resolve({ success });
      } else {
        reject();
      }
    });
    uploadRFC.InitUploadBSRFC();
    uploadRFC.SetParams(accountName, user, mode, data);
    Globals._rfcMgr.QueueRFC(uploadRFC);
  });
}

//// === directory load rfc

export function LoadDirectory(directory, mode) {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      reject();
      return;
    }

    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    const dirRFC = new QDirRFCJSON();
    const callbackObject = {
      DirResult: (response, mode, path) => {
        response = JSON.parse(response);
        let success = response["success"];
        if (success) {
          const data = response["data"];
          let processedResults = ProcessFileResultsByMode(data, mode);
          resolve({ success, response, path, processedResults });
        } else {
          var AP = new QAlert();
          AP.InitAlert(
            appstr("Alert"),
            "Unrecognized Transaction.",
            Globals._wsMgr,
            Globals._wsMgr
          );
          reject();
        }
      }
    };
    dirRFC.InitDirRFC(callbackObject, mode);
    dirRFC.SetParams(accountName, user, directory);
    Globals._rfcMgr.QueueRFC(dirRFC);
  });
}

const ProcessFileResultsByMode = (data, mode) => {
  var results = data;
  switch (mode) {
    case "Masters":
      var masters = data.filter(checkMaster);
      results = masters.map(function (item) {
        return item["filename"] + "." + item["extension"];
      });
      break;
    case "folders":
      if (data.length > 0) return ProcessFolderList(data);
      break;
    case "checkpoints":
      var checkpoints = data.filter(checkCheckPoints);
      results = checkpoints.map(function (item) {
        return item["filename"];
      });
      break;
    case "plans":
      var plans = data.filter(checkPlans);
      results = plans.map(function (item) {
        return item["filename"];
      });
      break;
    case "ssoConfig":
      var ssoConfigs = data.filter(checkSSOConfig);
      results = ssoConfigs.map(function (item) {
        return item["filename"] + "." + item["extension"];
      });
      break;
  }
  return results;
};

//--
export function DownloadMetadata() {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      reject();
      return;
    }

    var accountName = wsMgr.GetAccountName();
    var username = wsMgr.GetUserName();

    const syncRFC = new QDataRefreshRFC();
    const callbackObject = {
      DownloadMetadataDump: filename => {
        if (filename) {
          resolve({ filename });
        } else {
          reject();
        }
      }
    };
    syncRFC.InitQDataRefreshQuery(callbackObject);
    syncRFC.SetParamsNoChart(accountName, username, "dump", "");
    Globals._rfcMgr.QueueRFC(syncRFC);
  });
}

export function ChangeUserPassword(pword) {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      reject();
      return;
    }

    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();
    const email = wsMgr.GetUserEmail();

    const syncRFC = new QUpdatePassRFC();
    const callbackObject = {
      UpdatePassResult: response => {
        if (response) {
          resolve(response);
        } else {
          reject();
        }
      }
    };

    syncRFC.InitUpdatePassRFC(callbackObject);
    syncRFC.SetParams(user, accountName, pword, email);

    Globals._rfcMgr.QueueRFC(syncRFC);
  });
}

export function TestSharepointConnection(creds) {
  return new Promise(resolve => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      resolve({ success: false, error: "no_session" });
      return;
    }
    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    const rfc = new QTestSharepointConnectionRFC();
    const callbackObject = {
      HandleResponse: parsed => {
        resolve({
          success: !!parsed?.success,
          error: parsed?.error || (parsed?.success ? "" : "unknown")
        });
      }
    };
    rfc.InitTestSharepointConnectionRFC(callbackObject);
    rfc.SetParams(accountName, user, creds);
    Globals._rfcMgr.QueueRFC(rfc);
  });
}

export function ValidateDataSourceCreds(configuration, chartID) {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      reject();
      return;
    }
    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();
    const theData = JSON.parse(JSON.stringify(configuration));
    const validateCredsRFC = new QValidateCredsRFC();

    const callbackObject = {
      HandleResponse: response => {
        if (response) {
          resolve(response);
        } else {
          reject();
        }
      }
    };
    validateCredsRFC.InitQDataRefreshQuery(callbackObject);
    validateCredsRFC.SetParams(accountName, user, "validateCreds", theData, chartID);
    Globals._rfcMgr.QueueRFC(validateCredsRFC);
  });
}

export function LoadHeadersRFC(curChartObj, options = {}) {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      reject();
      return;
    }
    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    const loadType = options.type;
    const sourceIndex = options.index;
    const chartID = curChartObj.ID;
    const additionalFields = options.keys;
    const loadHeadersRFC = new QLoadHeadersRFC();

    const callbackObject = {
      ProcessHeaders: response => {
        if (response["success"]) {
          resolve({ response, loadType, sourceIndex, additionalFields });
        } else {
          reject();
        }
      }
    };
    loadHeadersRFC.InitLoadHeaders(
      callbackObject,
      loadType,
      chartID,
      sourceIndex,
      additionalFields
    );
    loadHeadersRFC.SetParams(accountName, user, { ...options }, chartID);
    Globals._rfcMgr.QueueRFC(loadHeadersRFC);
  });
}

export function GetSSOConfigs() {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      reject();
      return;
    }
    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    const callbackObject = {
      GetSSOConfigResult: (success, mapping) => {
        if (success) {
          resolve(mapping);
        } else {
          reject();
        }
      }
    };

    const SSORFC = new QSSOConfigRFC();
    SSORFC.InitGetSSOConfigRFC(callbackObject);
    SSORFC.SetParams(accountName, user, "getSSOConfig", "");
    Globals._rfcMgr.QueueRFC(SSORFC);
  });
}

export function UpdateSSOConfigs(ssoMapping) {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      reject();
      return;
    }
    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    const callbackObject = {
      GetSSOConfigResult: (success, mapping, code, message) => {
        if (success) {
          resolve({ success, mapping });
        } else {
          reject();
          const FailureAlert = new QAlert();
          // Validation codes carry an admin-facing reason; anything else is a generic failure.
          const validationFallback = {
            "0400": "Duplicate EntityID",
            "0410": "Invalid SSO Metadata"
          }[code];
          const text = validationFallback
            ? message || appstr(validationFallback)
            : appstr("Unable to Save Changes");
          FailureAlert.InitAlert(appstr("Alert"), text, null, wsMgr);
        }
      }
    };

    const data = JSON.stringify(ssoMapping);
    const SSORFC = new QSSOConfigRFC();
    SSORFC.InitGetSSOConfigRFC(callbackObject);
    SSORFC.SetParams(accountName, user, "doSetSSOConfig", data);
    Globals._rfcMgr.QueueRFC(SSORFC);
  });
}

function _scimTokenRFC(mode, extra) {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      reject();
      return;
    }
    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();
    const callbackObject = {
      ScimTokenResult: result => resolve(result || { success: false })
    };
    const rfc = new QScimTokenRFC();
    rfc.InitScimTokenRFC(callbackObject, mode);
    rfc.SetParams(accountName, user, extra);
    Globals._rfcMgr.QueueRFC(rfc);
  });
}

export function ListScimTokens() {
  return _scimTokenRFC("list");
}

export function CreateScimToken() {
  return _scimTokenRFC("create");
}

export function DeleteScimToken(tokenId) {
  return _scimTokenRFC("delete", { tokenId });
}

// Append Table RFCs & Record Look up

export function BuildAppendTableRFC(data, mode = null, chartID = null) {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr || mode == null) {
      reject();
      return;
    }
    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    const callbackObject = {
      HandleResponse: r => {
        let response = JSON.parse(r);
        if (response) {
          const data = response["info"];
          const theTable = data["table"];
          const overrides = data["overrides"];
          const code = response["code"];
          resolve({ data, theTable, overrides, code, response });
        } else {
          reject();
        }
      }
    };

    var loadTableRFC = new QBuildTableRFC();
    loadTableRFC.InitUpdateTables(callbackObject);
    loadTableRFC.SetParams(accountName, user, mode, JSON.stringify(data), chartID);
    Globals._rfcMgr.QueueRFC(loadTableRFC);
  });
}

export function DownloadTableRFC(data = {}, chartID = null) {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr || !chartID) {
      reject();
      return;
    }
    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    const callbackObject = {
      HandleResponse: r => {
        let response = JSON.parse(r);
        if (response) {
          resolve({ response });
        } else {
          reject();
        }
      }
    };

    var updateTableRFC = new QDownloadAppendTableRFC();
    updateTableRFC.InitUpdateTables(this);
    updateTableRFC.fAllowStacked = true;
    updateTableRFC.SetParams(
      accountName,
      user,
      "downloadAppendTable",
      JSON.stringify(data),
      chartID
    );
    Globals._rfcMgr.QueueRFC(updateTableRFC);
  });
}

export function LookupRecord(
  recordIDs,
  chartID,
  handleRejectResolve = true,
  handlePositions = false,
  fetchConfig = {
    getFirstHolder: false
  }
) {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!recordIDs || !wsMgr) {
      reject();
      return;
    }
    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();
    if (chartID == null) {
      reject();
      return;
    }

    var loadTableRFC = new QRecordLookupRFC();
    if (handleRejectResolve) {
      const callbackObject = {
        SvrQueryResult: (success, data) => {
          if (success) {
            resolve(data);
          } else {
            reject();
          }
        }
      };
      loadTableRFC.InitRecordLookup(callbackObject);
    } else {
      loadTableRFC.InitRecordLookup(null, resolve, reject);
    }

    loadTableRFC.SetParams(accountName, recordIDs, chartID, user, handlePositions, fetchConfig);
    Globals._rfcMgr.QueueRFC(loadTableRFC);
  });
}

export const UpdatePlans = (planData, mode) => {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      reject();
      return;
    }

    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    const callBackObj = {
      onResult: (success, response) => {
        if (success && mode === enumUpdatePlanModes.deleteplan && Globals._svrChartMgr) {
          if (planData["planID"] === Globals._svrChartMgr.GetLastPlanID()) {
            Globals._svrChartMgr.SetLastPlanID(null);
          }
        }
        resolve({ success, response });
      },
      fWSMgr: wsMgr,
      UpdatePanel: () => {}
    };

    const updatePlansRFC =
      mode === enumUpdatePlanModes.duplicateplan ? new QBuildPlanRFC() : new QUpdatePlansRFC();
    updatePlansRFC.InitUpdatePlans(callBackObj);
    updatePlansRFC.SetParams(accountName, user, mode, JSON.stringify(planData));
    Globals._rfcMgr.QueueRFC(updatePlansRFC);
  });
};

export const DeleteUsers = userIDList => {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      reject();
      return;
    }

    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    const callBackObj = {
      DeleteUsersResult: success => resolve({ success })
    };

    const deleteUsersRFC = new QDeleteUsersRFC();
    deleteUsersRFC.InitDeleteUsersRFC(callBackObj);
    deleteUsersRFC.SetParams(accountName, user, JSON.stringify(userIDList));
    Globals._rfcMgr.QueueRFC(deleteUsersRFC);
  });
};

export function DoReportRFC(mode, data = {}) {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      reject({});
      return;
    }
    const getUserDataRFC = new QReportsRFC();
    const callbackObject = {
      ReportResult: result => {
        if (result["success"]) {
          resolve({ result });
        } else {
          reject({ result });
        }
      }
    };
    getUserDataRFC.InitReportRFC(callbackObject);
    getUserDataRFC.SetParams(
      wsMgr.GetAccountName(),
      wsMgr.GetUserName(),
      mode,
      JSON.stringify(data)
    );
    Globals._rfcMgr.QueueRFC(getUserDataRFC);
  });
}

export function DoRenameExistingOpenDoc(newName) {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      reject();
      return;
    }
    const docMgr = wsMgr.GetDocMgr();
    const directoryName = docMgr.GetFileDirectory();
    const currentFilename = docMgr.GetFileName(true);
    const org = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    const renameDocRFC = new QFileRFC();
    const callbackObject = {
      FileResult: response => {
        let success = response["success"];
        if (!success) {
          var w = new QAlert();
          w.InitAlert(appstr("Alert"), appstr("renameerr"), null, Globals._wsMgr, true);
        }
        if (success) {
          resolve({ response });
        } else {
          reject();
        }
      }
    };
    renameDocRFC.InitFileRFC(callbackObject, "file", directoryName);
    renameDocRFC.SetParams(org, user, "rename", {
      curname: currentFilename,
      newname: newName,
      entitytype: "file",
      dir: directoryName
    });
    Globals._rfcMgr.QueueRFC(renameDocRFC);
  });
}

export function DoFileOperationAsync(mode, params, callbackObj = this) {
  return new Promise((resolve, reject) => {
    try {
      if (!params.entitytype) {
        throw new Error("entitytype is required");
      }

      const pathParam = params.thePath || params.dir;
      if (!pathParam) {
        throw new Error("thePath or dir is required");
      }

      const fileRFC = new QFileRFC();
      const entity = params.entitytype;
      const folder = pathParam;
      const fileName = params.names || params.curname || "";

      fileRFC.InitFileRFC(callbackObj, entity, folder, fileName, resolve, reject);

      const accountName = params.accountName;
      const username = params.username;

      if (!accountName || !username) {
        throw new Error("accountName and username are required");
      }

      fileRFC.SetParams(accountName, username, mode, params);
      Globals._rfcMgr.QueueRFC(fileRFC);
    } catch (error) {
      reject(error);
    }
  });
}

export function FetchAuditReport(options = {}) {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      reject();
      return;
    }
    const rfc = new QAuditReportRFC();
    rfc.InitAuditReportRFC({
      AuditReportResult: (success, payload) => {
        if (success) resolve(payload);
        else reject(payload);
      }
    });
    rfc.SetParams(wsMgr.GetAccountName(), wsMgr.GetUserName(), options);
    Globals._rfcMgr.QueueRFC(rfc);
  });
}

export function FetchAuditSnapshot() {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      reject();
      return;
    }
    const rfc = new QAuditSnapshotRFC();
    rfc.InitAuditSnapshotRFC({
      AuditSnapshotResult: (success, payload) => {
        if (success) resolve(payload);
        else reject(payload);
      }
    });
    rfc.SetParams(wsMgr.GetAccountName(), wsMgr.GetUserName());
    Globals._rfcMgr.QueueRFC(rfc);
  });
}

export function FetchAuditMeta() {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      reject();
      return;
    }
    const rfc = new QAuditMetaRFC();
    rfc.InitAuditMetaRFC({
      AuditMetaResult: (success, payload) => {
        if (success) resolve(payload);
        else reject(payload);
      }
    });
    rfc.SetParams(wsMgr.GetAccountName(), wsMgr.GetUserName());
    Globals._rfcMgr.QueueRFC(rfc);
  });
}
